import styled from '@xstyled/styled-components';
import React, { useState } from 'react';
import { Item, registry, RenderProps } from '../Item';
import { MESSAGE } from '../itemIds';
import tail from './tail.svg';
import { useEditorStore } from '~/shell/Store';
import LightButton from '~/editor/buttons/LightButton';
import { TrashIconForBtn } from '~/training/sequence/templates/items';
import DragAndDropIcon from '~/assets/button/dragAndDrop.svg';

const DragAndDropIconForBtn = styled(DragAndDropIcon)`
  cursor: move;
  margin-right: 10px;
  margin-top: 14px;
`;

const DraggableElement = styled.div`
  border-top: 2px solid transparent;
  border-bottom: 2px solid transparent;
  width: 100%;

  &.drag-over-top {
    border-top: 2px solid #9845fb !important;
    border-bottom: 2px solid transparent !important;
  }

  &.drag-over-bottom {
    border-top: 2px solid transparent !important;
    border-bottom: 2px solid #9845fb !important;
  }
`;

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

const Tail = styled(tail)`
  width: 22px;
  height: 23px;
  position: relative;
  margin-left: -15px;
  bottom: -15px;
  margin-top: -23px;
  fill: ${p => p.theme.colors.greyMed};
`;

const Sender = styled.divBox`
  font-size: 12px;
  line-height: 15px;
  color: ${p => p.theme.colors.greyDark};
`;

const TextMessage = styled.div`
  display: table;
  background-color: ${p => p.theme.colors.greyMed};
  border-radius: 20px;
  margin-top: 15px;
  padding: 10px;

  a {
    text-decoration: underline;
  }
`;

export class Message extends Item<typeof MESSAGE, Props> {
  protected render({ value, children, id }: RenderProps<Props>) {
    const [isDragOver, setIsDragOver] = useState(false);
    const [classNameForDragOver, setClassNameForDragOver] =
      useState<string>('');

    const sender = value?.sender || false;
    const { contentEditable, removeSubItem, sequence, apiService } =
      useEditorStore();

    const getCountOfItems = () =>
      sequence?.items?.filter((item: any) => item.type == 'LIST')[0]?.items
        ?.length;

    const handleDelete = () => {
      removeSubItem(id);
    };

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
      e.dataTransfer.setData('itemId', JSON.stringify({ id }));

      if (window) {
        window.sessionStorage.setItem('dragItemId', id);
      }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      setIsDragOver(false);
      const draggedData = JSON.parse(e.dataTransfer.getData('itemId'));
      const dragItemId = draggedData.id;

      if (!sequence) return;

      const listItem = sequence?.items?.find(
        (item: { type: string }) => item.type === 'LIST'
      );

      if (!listItem || !listItem.items) return;

      const dragIndex = listItem.items.findIndex(
        (item: { id: string }) => item.id == dragItemId
      );
      const dropIndex = listItem.items.findIndex(
        (item: { id: string }) => item.id == id
      );

      if (dragIndex === -1 || dropIndex === -1) return;

      const dragElement = document.querySelector(`[data-id="${dragItemId}"]`);
      const dropElement = document.querySelector(`[data-id="${id}"]`);

      if (dragItemId === id) return;

      if (!dragElement || !dropElement) return;

      const parent = dragElement.parentElement;
      if (!parent) return;

      if (dropIndex > dragIndex) {
        try {
          parent.insertBefore(dragElement, dropElement.nextSibling);
        } catch (error) {
          // Silently ignore DOM manipulation errors
        }
      } else {
        try {
          parent.insertBefore(dragElement, dropElement);
        } catch (error) {
          // Silently ignore DOM manipulation errors
        }
      }

      const newItems = [...listItem.items];
      const [draggedItem] = newItems.splice(dragIndex, 1);
      newItems.splice(dropIndex, 0, draggedItem);

      const updatedSequence = {
        ...sequence,
        items: sequence.items.map((item: { type: string; items?: any[] }) =>
          item.type === 'LIST' ? { ...item, items: newItems } : item
        )
      };

      newItems.forEach((item, index) => {
        if (item.id == dragItemId) {
          apiService?.reorderSubItem(item.id, index + 1);
        }
      });

      Object.assign(sequence, updatedSequence);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(true);

      if (window) {
        const dragItemId = window.sessionStorage.getItem('dragItemId');
        const currentId = e.currentTarget.dataset.id;

        const listItem = sequence?.items?.find(
          (item: { type: string }) => item.type === 'LIST'
        );

        if (!listItem || !listItem.items) return;

        const dragIndex = listItem.items.findIndex(
          (item: { id: string }) => item.id == dragItemId
        );
        const dropIndex = listItem.items.findIndex(
          (item: { id: string }) => item.id == currentId
        );

        if (dragIndex === dropIndex) {
          setClassNameForDragOver('');
        } else {
          setClassNameForDragOver(
            dropIndex > dragIndex ? 'drag-over-bottom' : 'drag-over-top'
          );
        }
      }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
    };

    return (
      <DraggableElement
        draggable={contentEditable}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-id={id}
        className={isDragOver ? classNameForDragOver : ''}
      >
        {sender && <Sender>{sender}</Sender>}
        <div>
          {contentEditable && <DragAndDropIconForBtn />}
          <TextMessage className="textMessageDarkTheme">
            {children}
            <Tail aria-hidden={true} />
          </TextMessage>
        </div>
        {contentEditable && getCountOfItems() > 1 && (
          <LightButton
            title="Remove"
            onClick={handleDelete}
            style={{
              padding: '11px',
              width: '38px',
              height: '38px',
              display: 'block',
              marginLeft: 'auto',
              marginTop: '5px'
            }}
          >
            <TrashIconForBtn
              style={{ marginLeft: '-5px', marginTop: '-5px' }}
            />
          </LightButton>
        )}
      </DraggableElement>
    );
  }
}

registry[MESSAGE] = Message;
